/* eslint-disable zeffy-linter-frontend/no-classname-prop -- legacy baseline (pre-rule); remove when these are migrated */
import * as React from "react";

import { Box, Theme } from "@mui/material";
import { ThemeProvider, lighten } from "@mui/material/styles";
import { FormCategory } from "@simplyk/common";
import classnames from "classnames";
import { useRouter } from "next/router";
import { useSnackbar } from "notistack";
import {
  WhatsappShareButton,
  FacebookShareButton,
  FacebookMessengerShareButton,
  LinkedinShareButton,
  TwitterShareButton,
  EmailShareButton,
} from "react-share";

import { AmplitudeEvents } from "../../../common/constants/amplitude";
import { useAmplitude } from "../../../common/hooks/amplitude/useAmplitude";
import { zeffyTheme } from "../../../common/theme/theme";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardIcon, DashboardIconProps } from "@/dashboard-design-system/Icon/DashboardIcon";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import {
  X as XIcon,
  Whatsapp as WhatsappIcon,
  Messenger as MessengerIcon,
  Email as MailIcon,
  LinkedIn as LinkedInIcon,
  Instagram as InstagramIcon,
  Facebook as FacebookIcon,
  ShareLink as ShareIcon,
} from "@/icons/social";

export enum ShareThisButton {
  Copy = "copy",
  Email = "email",
  Facebook = "facebook",
  Linkedin = "linkedin",
  Messenger = "messenger",
  X = "x",
  Whatsapp = "whatsapp",
  Instagram = "instagram",
}

export type ShareThisProps = React.PropsWithChildren<{
  palette?: "zeffy" | "default";
  description?: string;
  buttons?: ShareThisButton[];
  size?: DashboardIconProps["size"];
  title?: string;
  subTitle?: string;
  url?: string | null;
  overrides?: { facebookUrl?: string; messengerUrl?: string };
  classes?: {
    root?: string;
    subtitle?: string;
  };
  centered?: boolean;
  from?: "ReferralPage" | "ThankYouPage";
  spaceBetweenButton?: number;
  noMargin?: boolean;
  formCategory?: FormCategory | null;
  buttonSize?: number;
  iconColor?: string;
  circleColor?: string;
}>;

const BUTTON_SIZE = 32;

const getFlyingButtonStyles = (theme: Theme, buttonSize = BUTTON_SIZE, circleColor?: string) => ({
  height: buttonSize,
  width: buttonSize,
  backgroundColor: circleColor ? circleColor : lighten(theme.palette.primary.main, 0.9),
  borderRadius: "50%",
  cursor: "pointer",
  transition: "opacity 0.2s ease-in, margin-top 0.2s ease-in",
  "&:hover": {
    opacity: 0.8,
    marginTop: "-5px",
  },
});

const DefaultButtons: ShareThisProps["buttons"] = [
  ShareThisButton.Copy,
  ShareThisButton.Facebook,
  ShareThisButton.Messenger,
  ShareThisButton.Linkedin,
  ShareThisButton.X,
  ShareThisButton.Whatsapp,
  ShareThisButton.Email,
];

const InternalShareThis: React.FunctionComponent<Omit<ShareThisProps, "color">> = ({
  buttons = DefaultButtons,
  children,
  classes,
  description,
  size = "small",
  title,
  subTitle,
  url: propUrl,
  overrides,
  centered = false,
  from,
  spaceBetweenButton,
  noMargin,
  buttonSize = BUTTON_SIZE,
  formCategory,
  iconColor,
  circleColor,
}) => {
  const router = useRouter();
  const url = propUrl || `${process.env.NEXT_PUBLIC_FRONTEND_URL}/${router.locale}${router.asPath}`;
  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();
  const { logAmplitudeEvent, logAmplitudeEventCallback } = useAmplitude();

  const amplitudePayload = React.useMemo(() => {
    if (from !== "ReferralPage") {
      return { form_type: formCategory };
    }
  }, [formCategory, from]);

  const amplitudeEvent = React.useMemo(() => {
    if (from === "ReferralPage") {
      return AmplitudeEvents.ReferralSharingClicked;
    }
    if (from === "ThankYouPage") {
      return AmplitudeEvents.ThankYouPageSharingClicked;
    }
    return AmplitudeEvents.FormSharingClicked;
  }, [from]);

  const handleCopyLink = React.useCallback(() => {
    logAmplitudeEvent(amplitudeEvent, { ...amplitudePayload, social_platform: "Copy Link" });

    navigator.clipboard.writeText(url as string);
    enqueueSnackbar(t("dashboard", "common.linkCopied"), {
      vibe: "positive",
    });
  }, [url, enqueueSnackbar, t, logAmplitudeEvent, amplitudeEvent, amplitudePayload]);

  const copyButton = (
    <div onClick={handleCopyLink} className="shareButton" key="copy">
      <DashboardIcon vibe="primary" size={size} className="flyingButton">
        <ShareIcon />
      </DashboardIcon>
    </div>
  );

  const facebookButton = (
    <FacebookShareButton url={overrides?.facebookUrl || url} className="shareButton" key="facebook" type="button">
      <DashboardIcon
        vibe="primary"
        size={size}
        className="flyingButton"
        onClick={logAmplitudeEventCallback(amplitudeEvent, {
          ...amplitudePayload,
          social_platform: "Facebook",
        })}
      >
        <FacebookIcon />
      </DashboardIcon>
    </FacebookShareButton>
  );

  const messengerButton = process.env.NEXT_PUBLIC_FACEBOOK_APP_ID && (
    <FacebookMessengerShareButton
      url={overrides?.messengerUrl || url}
      appId={process.env.NEXT_PUBLIC_FACEBOOK_APP_ID}
      className="shareButton"
      key="messenger"
      type="button"
    >
      <DashboardIcon
        vibe="primary"
        size={size}
        className="flyingButton"
        onClick={logAmplitudeEventCallback(amplitudeEvent, {
          ...amplitudePayload,
          social_platform: "Messenger",
        })}
      >
        <MessengerIcon />
      </DashboardIcon>
    </FacebookMessengerShareButton>
  );

  const xButton = (
    <TwitterShareButton url={url} title={title} className="shareButton" key="twitter" type="button">
      <DashboardIcon
        vibe="primary"
        size={size}
        className="flyingButton"
        onClick={logAmplitudeEventCallback(amplitudeEvent, {
          ...amplitudePayload,
          social_platform: "X",
        })}
      >
        <XIcon />
      </DashboardIcon>
    </TwitterShareButton>
  );

  const linkedInButton = (
    <LinkedinShareButton url={url} className="shareButton" key="linkedIn" type="button">
      <DashboardIcon
        vibe="primary"
        size={size}
        className="flyingButton"
        onClick={logAmplitudeEventCallback(amplitudeEvent, {
          ...amplitudePayload,
          social_platform: "LinkedIn",
        })}
      >
        <LinkedInIcon />
      </DashboardIcon>
    </LinkedinShareButton>
  );

  const emailButton = (
    <EmailShareButton url={url} title={title} body={description} className="shareButton" key="email" type="button">
      <DashboardIcon
        vibe="primary"
        size={size}
        className="flyingButton"
        onClick={logAmplitudeEventCallback(amplitudeEvent, {
          ...amplitudePayload,
          social_platform: "Email",
        })}
      >
        <MailIcon />
      </DashboardIcon>
    </EmailShareButton>
  );

  const whatsappButton = (
    <WhatsappShareButton url={url} title={title} className="shareButton" key="whatsapp" type="button">
      <DashboardIcon
        vibe="primary"
        size={size}
        className="flyingButton"
        onClick={logAmplitudeEventCallback(amplitudeEvent, {
          ...amplitudePayload,
          social_platform: "WhatsApp",
        })}
      >
        <WhatsappIcon />
      </DashboardIcon>
    </WhatsappShareButton>
  );

  const instagramButton = (
    <DashboardIcon
      vibe="primary"
      size={size}
      className="flyingButton"
      onClick={logAmplitudeEventCallback(amplitudeEvent, {
        ...amplitudePayload,
        social_platform: "Instagram",
      })}
    >
      <InstagramIcon />
    </DashboardIcon>
  );

  return (
    <Box
      className={classnames("shareThis", classes?.root, {
        centered,
      })}
      suppressHydrationWarning
      sx={(theme) => ({
        "&.shareThis": {
          margin: noMargin ? 0 : theme.spacing(2, 0),
        },
        "&.centered": {
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        },
        "& .subtitle": {
          color: "white",
          marginBottom: theme.spacing(1.5),
        },
        "& .shareIcons": {
          display: "flex",
          flexDirection: "row",
          gap: theme.spacing(spaceBetweenButton || 1.5),
          justifyContent: "flex-start",
        },
        "& .shareButton": {
          height: buttonSize,
          width: buttonSize,
        },
        "& .flyingButton": getFlyingButtonStyles(theme, buttonSize, circleColor),
      })}
    >
      {subTitle && (
        <DashboardTypography variant="subtitle1" className={classnames("subtitle", classes?.subtitle)}>
          {subTitle}
        </DashboardTypography>
      )}
      <Box
        component="div"
        className="shareIcons"
        sx={{
          svg: iconColor
            ? {
                fill: iconColor,
              }
            : {},
        }}
      >
        {children}
        {buttons.map((button) => {
          if (button === ShareThisButton.Copy) {
            return copyButton;
          }
          if (button === ShareThisButton.Facebook) {
            return facebookButton;
          }
          if (button === ShareThisButton.Messenger) {
            return messengerButton;
          }
          if (button === ShareThisButton.X) {
            return xButton;
          }
          if (button === ShareThisButton.Linkedin) {
            return linkedInButton;
          }
          if (button === ShareThisButton.Email) {
            return emailButton;
          }
          if (button === ShareThisButton.Whatsapp) {
            return whatsappButton;
          }
          if (button === ShareThisButton.Instagram) {
            return instagramButton;
          }
        })}
      </Box>
    </Box>
  );
};

export const ShareThis: React.FunctionComponent<ShareThisProps> = ({ palette = "default", ...rest }) =>
  palette === "zeffy" ? (
    <ThemeProvider theme={zeffyTheme}>
      <InternalShareThis {...rest} />
    </ThemeProvider>
  ) : (
    <InternalShareThis {...rest} />
  );
